import { useEffect, useState } from 'react';

import { now } from 'helpers/date';
import { useApiUploadActions } from 'hooks/upload-actions';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { DynamicTypeInput } from 'components/Form/DynamicTypeInput/DynamicTypeInput';
import {
  Button,
  DatePicker,
  FilePicker,
  Input,
  InputGroup,
  ToggleSwitch,
  ToggleSwitchInput,
} from 'components/FormComponents';

/**
 * Read-only display component for extra_data when check is complete
 * Shows all extra_data values with labels from extra_result_details where available
 */
const ExtraDataReadOnlyDisplay = ({ extraData, extraResultDetails }) => {
  const { t } = useTranslation();

  const safeExtraData = extraData ?? {};
  const safeExtraResultDetails = extraResultDetails ?? [];

  const extraDataKeys = Object.keys(safeExtraData);

  if (extraDataKeys.length === 0) {
    return (
      <div className="secondary-text u-font-size-small">
        {t('admin.candidate.completionModal.noExtraData', 'No additional data recorded')}
      </div>
    );
  }

  // Build a map of field codes to their config from extra_result_details
  const fieldConfigMap = safeExtraResultDetails.reduce((acc, detail) => {
    if (detail?.code) {
      acc[detail.code] = detail;
    }
    return acc;
  }, {});

  // Format field name for display (convert snake_case to readable text)
  const formatFieldName = key => {
    if (!key || typeof key !== 'string') return 'Unknown Field';
    return key.replaceAll('_', ' ');
  };

  // Render value - similar to CandidateCheckValues.jsx renderValue
  const renderValue = value => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'true' : 'false';

    if (Array.isArray(value)) {
      if (value.length === 0) return '-';
      // Array of strings - display as comma-separated or list
      if (value.every(v => typeof v === 'string' || v === null || v === undefined)) {
        const filtered = value.filter(v => v !== null && v !== undefined);
        return filtered.length > 0 ? filtered.join(', ') : '-';
      }
      // Array of objects - stringify
      return JSON.stringify(value);
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  };

  return (
    <div className="d-flex flex-column u-gap-small">
      {extraDataKeys.map(key => {
        const value = safeExtraData[key];
        const fieldConfig = fieldConfigMap[key];
        const title = fieldConfig?.title ?? formatFieldName(key);

        return (
          <div key={key} className="d-flex flex-column">
            <span className="secondary-text u-font-size-small u-font-weight-600 u-capitalize">
              {title}
            </span>
            <span className="primary-text">{renderValue(value)}</span>
          </div>
        );
      })}
    </div>
  );
};

const ResultUploadSection = ({
  control,
  check,
  checkType,
  metadata,
  getUpload = () => {},
  signUpload = () => {},
  createUpload = () => {},
  deleteUpload = () => {},
  allowedTypes = { 'application/pdf': ['.pdf'] },
  result = false,
  title,
}) => (
  <InputGroup title={title}>
    <Controller
      control={control}
      name="upload_id"
      render={({ field: { onChange, value } }) => (
        <FilePicker
          onChange={onChange}
          value={value}
          metadata={metadata}
          accept={allowedTypes}
          modelParams={{
            candidate_id: check.candidate_id,
            candidate_check_id: check.id,
            ...(result && { result: true }),
          }}
          bucketFolder={
            check.type === 'acic_check'
              ? `acic/background_result/${check.type}`
              : `background_result/${check.type}`
          }
          getUpload={getUpload}
          signUpload={signUpload}
          createUpload={createUpload}
          deleteUpload={deleteUpload}
        />
      )}
    />
  </InputGroup>
);

export const CandidateCompletionModal = ({
  check,
  checkType,
  subCheck = null,
  visible,
  onUpdate = () => {},
  onClose = () => {},
  showPauseToggle = false,
}) => {
  const { t } = useTranslation();
  const isSubCheck = !!subCheck;
  const record = isSubCheck ? subCheck : check;

  const { reset, handleSubmit, control, register, watch, setValue } = useForm();

  const { getUpload, signUpload, createUpload, deleteUpload } = useApiUploadActions();

  const [manualResultUploadEnabled, setManualResultUploadEnabled] = useState(false);
  const [supportingDocumentUploadEnabled, setSupportingDocumentUploadEnabled] = useState(false);
  const [supportingDocumentUploadId, setSupportingDocumentUploadId] = useState(null);

  useEffect(() => {
    reset({
      upload_id: record.upload_id,
      result_flag: !!record.result_flag,
      expiration_date: record.expiration_date || '9999-12-31',
      has_report: !!checkType?.has_report,
      provider_id: record.provider_id,
      ...(isSubCheck
        ? { extra_details: subCheck.extra_details ?? {} }
        : { extra_data: check.extra_data ?? {} }),
    });
    // Reset supporting document upload ID when check/subCheck changes
    setSupportingDocumentUploadId(null);
  }, [check, subCheck]);

  // Persist everything currently in the form; normalize flags before sending.
  const persistAll = (patch = {}, generateReport = false) => {
    const snapshot = { ...watch(), ...patch };
    const normalized = {};

    if ('upload_id' in snapshot && snapshot.upload_id != null) {
      normalized.upload_id = snapshot.upload_id;
    }

    if (checkType?.has_job && checkType?.has_provider_id && 'provider_id' in snapshot) {
      normalized.provider_id = snapshot.provider_id; // allow '' to clear it server-side
    }

    if (checkType?.has_expiration_date && 'expiration_date' in snapshot) {
      normalized.expiration_date =
        snapshot.expiration_date === '' ? null : snapshot.expiration_date;
    }

    const extraKey = isSubCheck ? 'extra_details' : 'extra_data';
    if (extraKey in snapshot) {
      // When check is already complete, don't overwrite extra_data from form
      // This prevents mapped field values (from result_fields_mapping) from being overwritten
      // by stale form values that may have different option sets
      if (record.completed_at) {
        // Keep original data unchanged for completed checks
        const originalData = isSubCheck
          ? (subCheck?.extra_details ?? {})
          : (check?.extra_data ?? {});
        if (Object.keys(originalData).length > 0) {
          normalized[extraKey] = originalData;
        }
      } else {
        // Merge with original data to preserve fields not shown in the form
        const originalData = isSubCheck
          ? (subCheck?.extra_details ?? {})
          : (check?.extra_data ?? {});
        normalized[extraKey] = { ...originalData, ...(snapshot[extraKey] || {}) };
      }
    }

    if (checkType?.has_result_flag && 'result_flag' in snapshot) {
      normalized.result_flag = !!snapshot.result_flag;
    }

    // Include completed_at if it's in the snapshot
    if ('completed_at' in snapshot) {
      normalized.completed_at = snapshot.completed_at;
    }

    onUpdate(normalized, generateReport);
  };

  const onSubmit = data => {
    if (!record.completed_at) data.completed_at = now();
    persistAll(data);
  };

  const createSupportingDocumentUpload = async uploadData => {
    const result = await createUpload(uploadData);
    // Don't set upload_id for supporting documents - only result uploads should set upload_id
    return result;
  };

  const saveDetails = data => {
    persistAll({}, false);
  };

  const saveDetailsAndGenerateReport = data => {
    persistAll({}, true);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <AnimatedModal
      visible={visible}
      onClose={handleClose}
      title={`Complete ${record?.title || checkType?.title}`}
      icon="check"
      small>
      {!isSubCheck && showPauseToggle && !watch('upload_id') && (
        <Controller
          control={control}
          name="extra_data.document_request_paused"
          render={({ field: { onChange, value } }) => (
            <ToggleSwitch
              label={t('admin.candidate.completionModal.pauseAutomatedDocumentRequests')}
              inputProps={{
                checked: !!value,
                onChange: e => {
                  const checked = e.target.checked;
                  const currentExtraData = watch('extra_data') || {};
                  const updatedExtraData = {
                    ...currentExtraData,
                    document_request_paused: checked,
                  };
                  onChange(checked);
                  setValue('extra_data', updatedExtraData);
                  // Persist immediately to backend
                  persistAll({ extra_data: updatedExtraData }, false);
                },
              }}
              status={
                value
                  ? { text: t('admin.candidate.completionModal.paused'), status: 'warning' }
                  : { text: t('admin.candidate.completionModal.active'), status: 'success' }
              }
            />
          )}
        />
      )}
      <ToggleSwitch
        name="has_report"
        register={register}
        label={t('admin.candidate.completionModal.reportType')}
        status={
          watch('has_report')
            ? { text: t('admin.candidate.completionModal.automaticReport'), status: 'neutral' }
            : { text: t('admin.candidate.completionModal.manualReport'), status: 'neutral' }
        }
      />
      {watch('has_report') && (
        <>
          <ToggleSwitchInput
            text={t('admin.candidate.completionModal.uploadSupportingDocument')}
            subtext={t('admin.candidate.completionModal.uploadSupportingDocumentDescription')}
            onChange={e => setSupportingDocumentUploadEnabled(e.target.checked)}
            className="u-margin-top"
            noMaxWidth
            value={supportingDocumentUploadEnabled}>
            <InputGroup title={t('admin.candidate.completionModal.attachSupportingDocument')}>
              <FilePicker
                onChange={uploadId => {
                  // Update component state (not form state) so FilePicker can display/delete it
                  // but it won't be saved to the check's upload_id field
                  setSupportingDocumentUploadId(uploadId);
                }}
                value={supportingDocumentUploadId}
                metadata={{
                  ...(isSubCheck
                    ? { check_type: check.type, sub_check_id: subCheck.id }
                    : { check_type: check.type }),
                }}
                accept={{ 'application/pdf': ['.pdf'], 'image/*': ['.jpeg', '.png'] }}
                modelParams={{
                  candidate_id: check.candidate_id,
                  candidate_check_id: check.id,
                }}
                bucketFolder={
                  check.type === 'acic_check'
                    ? `acic/background_result/${check.type}`
                    : `background_result/${check.type}`
                }
                getUpload={getUpload}
                signUpload={signUpload}
                createUpload={createSupportingDocumentUpload}
                deleteUpload={deleteUpload}
              />
            </InputGroup>
          </ToggleSwitchInput>
        </>
      )}
      {visible && (
        <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column">
          {!watch('has_report') && (
            <ToggleSwitchInput
              text={t('admin.candidate.completionModal.uploadReportManually')}
              subtext={t('admin.candidate.completionModal.uploadReportManuallyDescription')}
              onChange={e => setManualResultUploadEnabled(e.target.checked)}
              noMaxWidth
              value={manualResultUploadEnabled}>
              {manualResultUploadEnabled && (
                <ResultUploadSection
                  control={control}
                  check={check}
                  checkType={checkType}
                  metadata={{
                    ...(isSubCheck
                      ? { sub_result: check.type, sub_check_id: subCheck.id }
                      : { result: check.type }),
                  }}
                  result={true}
                  getUpload={getUpload}
                  signUpload={signUpload}
                  createUpload={createUpload}
                  deleteUpload={deleteUpload}
                  title={t('admin.candidate.completionModal.uploadReportManuallyTitle')}
                />
              )}
            </ToggleSwitchInput>
          )}
          {checkType?.has_result_flag && (
            <ToggleSwitch
              name="result_flag"
              register={register}
              label={t('admin.candidate.completionModal.hasResult')}
              status={
                watch('result_flag')
                  ? {
                      text:
                        checkType?.true_flag_label ||
                        t('admin.candidate.completionModal.resultFound'),
                      status: 'error',
                    }
                  : {
                      text:
                        checkType?.false_flag_label ||
                        t('admin.candidate.completionModal.noResultFound'),
                      status: 'success',
                    }
              }
            />
          )}
          {checkType?.has_job && checkType?.has_provider_id && (
            <InputGroup title={t('admin.candidate.completionModal.providerId')}>
              <Input
                name="provider_id"
                type="text"
                placeholder={t('admin.candidate.completionModal.enterProviderId')}
                defaultValue={check?.provider_id}
                register={register}
                onBlur={() => persistAll()}
              />
            </InputGroup>
          )}
          {(checkType?.has_expiration_date || checkType?.extra_result_details?.length > 0) && (
            <div className="u-border-radius background-primary u-border u-padding u-padding-top d-flex flex-column">
              <span className="title-4 u-font-weight-600 primary-text">
                {t('admin.candidate.completionModal.additionalDetails')}
              </span>
              <br />
              {checkType?.has_expiration_date && (
                <InputGroup title={t('admin.candidate.completionModal.expirationDate')}>
                  <Controller
                    control={control}
                    name="expiration_date"
                    render={({ field: { onChange, value } }) => (
                      <DatePicker
                        value={value}
                        onChange={onChange}
                        outputFormat="YYYY-MM-DD"
                        useDefaultToday={false}
                      />
                    )}
                  />
                </InputGroup>
              )}

              {record.completed_at ? (
                /* Read-only display when check is already complete - prevents overwriting mapped values */
                <ExtraDataReadOnlyDisplay
                  extraData={isSubCheck ? subCheck?.extra_details : check?.extra_data}
                  extraResultDetails={checkType?.extra_result_details}
                />
              ) : (
                /* Editable form fields when check is not yet complete */
                checkType?.extra_result_details?.map(extraDetail => (
                  <DynamicTypeInput
                    key={extraDetail?.code ?? extraDetail?.title ?? 'unknown'}
                    title={extraDetail?.title ?? ''}
                    placeholder={extraDetail?.placeholder}
                    defaultValue={extraDetail?.default_value}
                    fieldName={`${isSubCheck ? 'extra_details' : 'extra_data'}.${extraDetail?.code ?? ''}`}
                    type={extraDetail?.type}
                    fieldOptions={extraDetail?.options}
                    useDefault={extraDetail?.use_default}
                    control={control}
                    register={register}
                    watch={watch}
                  />
                ))
              )}
            </div>
          )}

          {/* Only show Update Details button when check is not complete and has editable fields */}
          {!record.completed_at &&
            (checkType?.extra_result_details?.length > 0 || checkType?.has_provider_id) && (
              <div className="d-flex u-flex-wrap u-gap-medium u-flex-align-center u-margin-top">
                <Button
                  type="secondary"
                  large
                  className="flex-grow-1"
                  onClick={handleSubmit(saveDetails)}>
                  {t('admin.candidate.completionModal.updateDetails')}
                </Button>
              </div>
            )}

          <div className="d-flex u-flex-wrap u-gap-medium u-flex-align-center u-margin-top">
            {watch('has_report') && (
              <Button
                type="secondary"
                large
                className="flex-grow-1"
                onClick={handleSubmit(saveDetailsAndGenerateReport)}>
                {t('admin.candidate.completionModal.generateAutomaticReport')}
              </Button>
            )}
            <Button submit type="primary" large className="flex-grow-1">
              {record.completed_at ? t('common.update') : t('common.complete')}{' '}
              {isSubCheck ? t('common.subCheck') : t('common.check')}
            </Button>
          </div>
        </form>
      )}
    </AnimatedModal>
  );
};
